import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ExternalLink, AlertCircle, Loader2, BookOpen } from 'lucide-react';
import type { Source, ModuleRef } from '@/lib/models/source';
import { SourceKindLabels } from '@/lib/models/source';
import { sourcesApi } from '@/lib/api/sourcesApi';
import { SourceIcon } from './SourceIcon';
import { formatBytes, formatRelativeDate } from './sourceFormat';
import { DocumentPreviewShell } from '@/components/custom-ui/DocumentPreviewShell';

// SourceViewerModal — opens a source for inspection. One modal,
// kind-aware renderer:
//
//   kind="pdf"   → iframe with the bytes loaded as a blob: URL
//                  (auth header doesn't survive native iframe src,
//                  so we fetch + createObjectURL up front).
//   kind="image" → <img> with the same blob: URL.
//   kind="text"  → <pre> with the blob's text content.
//   kind="url"   → "Open in new tab" button + best-effort iframe;
//                  many sites block embedding via X-Frame-Options.
//
// Triggered from both surfaces (chat sidebar + dashboard library
// page) by setting open-state to a Source and rendering this. Caller
// owns the state; close clears it.
//
// Phase 4 — shipped 2026-06-01. The viewer is intentionally lean
// (no PDF page navigation, no syntax highlighting on text, no
// zoom/pan on images). Iterate when actual UX friction surfaces.

interface Props {
    source: Source;
    onClose: () => void;
}

export const SourceViewerModal: React.FC<Props> = ({ source, onClose }) => {
    // Blob lifecycle: fetch on mount, revoke the object URL on unmount
    // OR when the source changes (caller switches between sources
    // without closing the modal — rare but possible). URL sources
    // skip the fetch entirely.
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [textContent, setTextContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // "Used in" panel — lazy fetch when count > 0. Empty array for
    // sources nothing references; null while loading. Failures fall
    // back to null silently — the panel just doesn't show; the table
    // chip already conveyed the count.
    const [usedIn, setUsedIn] = useState<ModuleRef[] | null>(null);

    useEffect(() => {
        // URL sources don't have a download; nothing to fetch.
        if (source.kind === 'url') {
            setBlobUrl(null);
            setTextContent(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        setBlobUrl(null);
        setTextContent(null);

        let cancelled = false;
        let createdUrl: string | null = null;
        sourcesApi
            .downloadSourceBlob(source.id)
            .then(async (blob) => {
                if (cancelled) return;
                if (source.kind === 'text') {
                    // For text we read the contents directly; no
                    // object URL needed. Keeps the dom render simple.
                    const txt = await blob.text();
                    if (cancelled) return;
                    setTextContent(txt);
                } else {
                    createdUrl = URL.createObjectURL(blob);
                    setBlobUrl(createdUrl);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message || 'Kunde inte ladda källan.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [source.id, source.kind]);

    // Fetch the "used in" module list when the count says it's worth
    // it. Skipped when used_in_count === 0 (no roundtrip on cold-storage
    // sources). Errors are swallowed — the table chip already conveyed
    // the count, so a failed fetch isn't catastrophic.
    useEffect(() => {
        if (source.used_in_count <= 0) {
            setUsedIn([]);
            return;
        }
        let cancelled = false;
        sourcesApi
            .listModulesUsingSource(source.id)
            .then((refs) => {
                if (!cancelled) setUsedIn(refs);
            })
            .catch(() => {
                if (!cancelled) setUsedIn(null);
            });
        return () => {
            cancelled = true;
        };
    }, [source.id, source.used_in_count]);

    return (
        <DocumentPreviewShell
            onClose={onClose}
            icon={<SourceIcon kind={source.kind} className="w-5 h-5 flex-shrink-0" />}
            title={source.original_name}
            subtitle={
                <>
                    {SourceKindLabels[source.kind]}
                    {source.size_bytes != null && ` · ${formatBytes(source.size_bytes)}`}
                    {' · '}
                    Uppladdad {formatRelativeDate(source.created_at)}
                    {source.is_promoted && ' · org'}
                </>
            }
            headerActions={
                source.kind === 'url' && source.url ? (
                    <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#1A4D4F] hover:bg-gray-100 rounded-lg"
                    >
                        <ExternalLink className="w-3.5 h-3.5" /> Öppna i ny flik
                    </a>
                ) : undefined
            }
            footer={
                usedIn && usedIn.length > 0 ? (
                    <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3">
                        <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="w-4 h-4 text-gray-500" />
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                Används i {usedIn.length} {usedIn.length === 1 ? 'modul' : 'moduler'}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {usedIn.map((m) => (
                                <RouterLink
                                    key={m.id}
                                    to={`/dashboard/onboarding/modules/${m.id}`}
                                    onClick={onClose}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-[#1A4D4F]/10 hover:text-[#1A4D4F] text-gray-700 rounded transition-colors"
                                >
                                    {m.module_name}
                                </RouterLink>
                            ))}
                        </div>
                    </div>
                ) : undefined
            }
        >
            {loading ? (
                        <div className="h-full flex items-center justify-center text-gray-500">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laddar källan…
                        </div>
                    ) : error ? (
                        <div className="h-full flex flex-col items-center justify-center gap-2 p-8 text-center">
                            <AlertCircle className="w-8 h-8 text-red-500" />
                            <p className="text-sm text-gray-700">{error}</p>
                        </div>
                    ) : source.kind === 'pdf' && blobUrl ? (
                        <iframe
                            src={blobUrl}
                            title={source.original_name}
                            className="w-full h-full"
                        />
                    ) : source.kind === 'image' && blobUrl ? (
                        <div className="h-full overflow-auto flex items-center justify-center p-4">
                            <img
                                src={blobUrl}
                                alt={source.original_name}
                                className="max-w-full max-h-full object-contain"
                            />
                        </div>
                    ) : source.kind === 'text' && textContent != null ? (
                        <pre className="h-full overflow-auto p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap">
                            {textContent}
                        </pre>
                    ) : source.kind === 'url' && source.url ? (
                        // Best-effort iframe — many sites block via
                        // X-Frame-Options or Content-Security-Policy.
                        // The "Öppna i ny flik" link in the header is
                        // the always-works fallback.
                        <iframe
                            src={source.url}
                            title={source.original_name}
                            className="w-full h-full"
                            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                        />
                    ) : (
                        <div className="h-full flex items-center justify-center text-sm text-gray-500">
                            Inget innehåll att visa.
                        </div>
                    )}
        </DocumentPreviewShell>
    );
};
